import { z } from 'zod';

// Governed enums + jsonb content contracts for the `practice_build_decisions`
// decision record (see `../db/schema.ts`) — one row per build_practice tool
// invocation.
//
// Each constrained string domain is a single source of truth (ADR-056 Rule 8):
// the `const X = [...] as const` array drives BOTH the `text`+CHECK constraint
// rendered in the schema and the wire/contract schema here, so the DB and the
// validators cannot drift. The jsonb schemas type the columns via `.$type<>()`
// per ADR-066 Rule 2 and validate at the repo boundary.

// ─── tool name ───────────────────────────────────────────────────────────────
// The provider-facing tool name — the string the voice model calls, the
// agent-tool-set gate checks, the transcript export matches on, and the
// telemetry `tool` tag carries. One definition so the four cannot drift.
export const BUILD_PRACTICE_TOOL_NAME = 'build_practice' as const;

// ─── pathway ─────────────────────────────────────────────────────────────────
// Which builder pathway produced the decision: `base` is the standing
// base-conversation builder; `journey` is reserved for the journey composer.
export const PRACTICE_BUILD_PATHWAY = ['base', 'journey'] as const;
export const practiceBuildPathwaySchema = z.enum(PRACTICE_BUILD_PATHWAY);
export type PracticeBuildPathway = z.infer<typeof practiceBuildPathwaySchema>;

// ─── outcome ─────────────────────────────────────────────────────────────────
// The decision the builder reached. A refusal is a well-formed answer, not an
// error — the never-error wire contract has exactly these two arms.
export const PRACTICE_BUILD_OUTCOME = ['built', 'refused'] as const;
export const practiceBuildOutcomeSchema = z.enum(PRACTICE_BUILD_OUTCOME);
export type PracticeBuildOutcome = z.infer<typeof practiceBuildOutcomeSchema>;

// ─── verification path ───────────────────────────────────────────────────────
// How a proposed technique was verified on the journey pathway, where the
// planner names the technique and the builder checks it instead of choosing:
// `code` is the structural check every technique passes (gated set and
// structured vetoes, no model call); `llm` adds one bounded model judgement
// against the technique's own clauses, reserved for the higher-risk entries.
export const PRACTICE_VERIFY_PATHS = ['code', 'llm'] as const;
export const practiceVerifyPathSchema = z.enum(PRACTICE_VERIFY_PATHS);
export type PracticeVerifyPath = z.infer<typeof practiceVerifyPathSchema>;

// ─── selection (decision-record internals) ───────────────────────────────────
// What the selection step happened to produce, recorded on the decision row
// only — selection internals never return to the caller. Optional-tolerant by
// design: a timeout or out-of-schema output yields little, so absence of any
// field is never a validation failure.
export const practiceBuildSelectionSchema = z.object({
  /**
   * The technique the caller proposed (journey pathway: the planner's pick),
   * recorded whether the verification accepted or refused it. Absent on the
   * base pathway, where the selector chooses.
   */
  proposedTechnique: z.string().optional(),
  /** Which verification the proposed technique went through. Absent when nothing was proposed. */
  verifyPath: practiceVerifyPathSchema.optional(),
  /** The selector's stated reason for its pick (or its refusal). */
  rationale: z.string().optional(),
  /** Second-choice technique key, when the selector named one. */
  runnerUp: z.string().optional(),
  /** Selector-reported confidence, as emitted (numeric score or named tier). */
  confidence: z.union([z.number(), z.string()]).optional(),
  /** Size of the post-gate eligible set the selector chose from. */
  eligibleCount: z.number().int().nonnegative().optional(),
  /** Technique keys in the post-gate eligible set. */
  eligibleKeys: z.array(z.string()).optional(),
  /**
   * Classification of the raw model output (e.g. valid, out_of_schema,
   * out_of_set, timeout). Open string — failure classes evolve with the
   * selector, and a new class must not require a schema change.
   */
  rawOutputClass: z.string().optional(),
});
export type PracticeBuildSelection = z.infer<typeof practiceBuildSelectionSchema>;

// ─── response payload (the two wire arms) ────────────────────────────────────
// The exact arm returned to the caller, stored verbatim so an idempotent
// replay returns the stored arm without re-running selection. Field names are
// the frozen flat snake_case set the voice model reads — a stored jsonb blob
// returned verbatim keeps its own keys (the ADR-066 Rule 3 carve-out from the
// camelCase wire convention). `.strict()` pins the frozen field set: an extra
// field (e.g. `title`) is a contract violation, not an extension point.
export const practiceBuiltArmSchema = z
  .object({
    status: z.literal('built'),
    practice_id: z.string(),
    technique: z.string(),
    technique_version: z.string().min(1),
    /** The member's verbatim intent sentence, embedded by template fill. */
    intent: z.string(),
    instructions: z.string(),
    never: z.string(),
    done_when: z.string(),
    /**
     * Sona's next utterance, spoken as written — the one signed verbatim
     * member-facing field. Nullable by design: a technique whose opening is
     * compositional (`firstMove: null` on the catalogue entry) has no signed
     * verbatim opener, and the builder must never fabricate one at runtime
     * (LLM-free assembly, no unsigned member-facing text). When null, the
     * `instructions` govern the opening — the discipline prompt binds
     * first_move as "your next utterance, spoken as written" only when
     * present.
     */
    first_move: z.string().nullable(),
  })
  .strict();
export type PracticeBuiltArm = z.infer<typeof practiceBuiltArmSchema>;

export const practiceRefusedArmSchema = z
  .object({
    status: z.literal('refused'),
    reason: z.string(),
    instead: z.string(),
  })
  .strict();
export type PracticeRefusedArm = z.infer<typeof practiceRefusedArmSchema>;

export const practiceBuildResponsePayloadSchema = z.discriminatedUnion('status', [
  practiceBuiltArmSchema,
  practiceRefusedArmSchema,
]);
export type PracticeBuildResponsePayload = z.infer<typeof practiceBuildResponsePayloadSchema>;

// ─── analytics failure classes ───────────────────────────────────────────────
// The refusal classes that count as FAILURES in product analytics: the
// machinery or contract broke (timeout, out-of-schema output, unresolvable
// session, upstream error), even though the wire answer is still a 200
// refused arm. A refusal NOT in this set is a well-formed answer — the
// builder evaluated the intent and said "no fit" (selector refusal, thin
// intent, empty eligible set) — and charts as a succeeded outcome with
// `status: refused`. Closed enum: the analytics forwarder lifts
// `failure_class` only through a bounded schema, so a new class must be
// added here before it becomes chartable (an unknown class defaults to the
// refused-succeeded side rather than silently widening this vocabulary).
export const PRACTICE_BUILD_FAILURE_CLASSES = [
  'invalid_request',
  'session_unresolved',
  'tool_not_in_agent_set',
  'db_unavailable',
  'decision_write_failed',
  'selection_timeout',
  'selection_error',
  'out_of_schema',
  'out_of_set',
  'internal_error',
  // The verification step's machinery failures — the journey-pathway twins of
  // the three selection failures above. A verifier REJECTION is not here: like
  // a selector refusal it is a well-formed answer, not a broken contract.
  'verify_timeout',
  'verify_error',
  'verify_out_of_schema',
] as const;
export const practiceBuildFailureClassSchema = z.enum(PRACTICE_BUILD_FAILURE_CLASSES);
export type PracticeBuildFailureClass = z.infer<typeof practiceBuildFailureClassSchema>;

export function isPracticeBuildFailureClass(value: string): value is PracticeBuildFailureClass {
  return PRACTICE_BUILD_FAILURE_CLASSES.some((failureClass) => failureClass === value);
}
